import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  DatePicker,
  Table,
  Typography,
  Spin,
  Empty,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  getPaymentRecordList,
  getRentContractDetail,
  getRentCollectionList,
} from "../api/rent-collection";

import { toBjNoon, fmtBj } from "../utils/bjDate";
import RentBillList from "../components/host/RentBillList";
import DetailTopBar from "../components/host/DetailTopBar";
import ContractInfoPanel from "../components/host/ContractInfoPanel";
import { rcdCss } from "../components/host/rentDetailStyle";
import { scrollToTop, findScrollParent } from "../utils/scroll";
import PaymentRecordList from "../components/host/PaymentRecordList";
import {
  moveOutRentContract,
  moveOutScheduledRentContract,
  checkRentContract,
} from "../api/host";

// ─────────────────────────────────────────────

const MoveOutDateModal = ({ open, onCancel, onSubmit, loading = false }) => {
  const [date, setDate] = useState();
  useEffect(() => {
    if (open) {
      setDate(dayjs().add(7, "day"));
    } else {
      setDate(null);
    }
  }, [open]);
  const handleOk = async () => {
    if (!date) {
      message.warning("请选择退房日期");
      return;
    }

    await onSubmit?.(toBjNoon(date));
  };

  const handleClose = () => {
    onCancel?.();
  };

  return (
    <Modal
      title="请选择退房日期"
      open={open}
      onCancel={handleClose}
      onOk={handleOk}
      confirmLoading={loading}
      okText="确认预约"
      cancelText="取消"
    >
      <div style={{ padding: "12px 0" }}>
        <DatePicker
          style={{ width: "100%" }}
          value={date}
          required
          format="YYYY-MM-DD"
          onChange={setDate}
          disabledDate={(current) =>
            current && current < dayjs().startOf("day")
          }
        />
      </div>
    </Modal>
  );
};
export default function RentCollectionDetail({
  listRecord,
  filters,
  refreshKey,
  onBack,
  showContractModal,
}) {
  const pageRef = useRef(null);
  const [contractId, setContractId] = useState(
    listRecord?._id?.toString() || listRecord?.contractId,
  );
  const [contractLoading, setContractLoading] = useState(false);
  const [contract, setContract] = useState(null);
  const [contractList, setContractList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("bills");
  const [paymentsVisited, setPaymentsVisited] = useState(false);
  // 预约退房暂不启用（等业务思路理清后再放回操作条）
  const [moveOutDateModalOpen, setMoveOutDateModalOpen] = useState(false);

  // 当前在待处理列表中的位置：由 contractId 推导，不再单独维护 index state
  const currentIndex = contractList.indexOf(String(contractId));

  function onEdit() {
    showContractModal(contract);
  }
  const handleMoveOutDateModalOk = async (date) => {
    setLoading(true);
    try {
      const res = await moveOutScheduledRentContract(contractId, {
        moveOutDate: date,
      });
      if (res.status === 200) {
        message.success("已预约退房");
        loadContractDetail();
      } else {
        message.error(res.msg);
      }
      setMoveOutDateModalOpen(false);
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "操作失败");
    } finally {
      setLoading(false);
    }
  };

  const doCheckOut = async () => {
    setLoading(true);
    try {
      const res = await moveOutRentContract(contractId);
      if (res.status === 200) {
        message.success("已退房");
        loadContractDetail();
      } else {
        message.error(res.msg);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || e?.message || "操作失败");
    } finally {
      setLoading(false);
    }
  };
  // 退房不可撤销，操作条里紧挨着编辑按钮，触屏容易误触，所以二次确认
  const handleCheckOut = () => {
    Modal.confirm({
      title: "确认退房？",
      content: `${contract?.host?.roomNumber || ""} 将立即办理退房，此操作不可撤销。`,
      okText: "确认退房",
      okButtonProps: { danger: true },
      cancelText: "取消",
      onOk: doCheckOut,
    });
  };

  // 外层 <main class="ant-layout-content"> 自带 padding-bottom 和灰底 (#f5f5f5)，会在页面底部露出一条。
  // 把「页面 → 滚动容器」之间所有祖先的底色刷成与页面渐变末端一致，离开时还原。
  useEffect(() => {
    if (!pageRef.current) return;
    const target = findScrollParent(pageRef.current);
    const nodes = [];
    let n = pageRef.current.parentElement;
    while (n && n !== document.body && nodes.length < 8) {
      nodes.push(n);
      if (n === target) break;
      n = n.parentElement;
    }
    if (target === window) nodes.push(document.documentElement, document.body);
    const prev = nodes.map((el) => el.style.backgroundColor);
    nodes.forEach((el) => {
      el.style.backgroundColor = "#e9f0f8";
    });
    return () => nodes.forEach((el, k) => (el.style.backgroundColor = prev[k]));
  }, []);

  // 进入页面 / 切换合约：加载详情并回到顶部
  useEffect(() => {
    if (!contractId) return;
    loadContractDetail();
    scrollToTop(pageRef.current);
  }, [contractId]);
  // 父组件通知刷新（例如合约编辑保存后）
  useEffect(() => {
    if (refreshKey) loadContractDetail();
  }, [refreshKey]);
  // 仅进入时拉一次待处理列表（快照，翻页过程中不刷新，index 保持稳定）
  useEffect(() => {
    getContractList();
  }, []);

  const detailSeq = useRef(0);
  const loadContractDetail = async () => {
    const seq = ++detailSeq.current;
    setContractLoading(true);
    try {
      const res = await getRentContractDetail(contractId);
      if (seq !== detailSeq.current) return; // 过期响应丢弃
      if (res.data.code === 0) setContract(res.data.data);
      else message.error(res.data.message || res.data.msg || "加载合约失败");
    } catch (e) {
      if (seq === detailSeq.current)
        message.error(e?.response?.data?.message || "加载合约失败");
    } finally {
      if (seq === detailSeq.current) setContractLoading(false);
    }
  };
  const getContractList = async () => {
    try {
      const res = await getRentCollectionList({
        roomNumber: filters?.roomNumber,
        contractStatus: filters?.contractStatus,
        overdueStatus: filters?.overdueStatus,
        page: 1,
        pageSize: 500,
        sample: 1,
      });
      if (res.status === 200) {
        setContractList(res.data.data.list.map((item) => String(item._id)));
      }
    } catch (e) {
      // 列表拉取失败只影响上一条/下一条，不打扰
    }
  };
  const goTo = (i) => {
    if (i < 0 || i >= contractList.length) return;
    setContract(null); // 清掉旧合约，避免切换时先闪一下旧数据
    setContractId(contractList[i]);
  };
  const checkContract = async () => {
    if (contract?.status === "pendingSettlement") {
      // 先让后端检查是否已结清（结清则置为已结算）
      try {
        const res = await checkRentContract(contractId);
        if (res.status === 200) {
          if (res.data.code === 0) message.success(res.data.msg);
          else message.warning(res.data.msg);
        }
      } catch (e) {
        message.error(e?.response?.data?.message || e?.message || "操作失败");
      }
    }
    // 统一重新拉取详情：check 接口返回的合约没有关联房间、客户、签约人，直接 setContract 会丢掉这些信息
    loadContractDetail();
  };

  return (
    <div className="rcd-page" ref={pageRef}>
      <style>{rcdCss}</style>

      <DetailTopBar
        onBack={onBack}
        index={currentIndex}
        total={contractList.length}
        onPrev={() => goTo(currentIndex - 1)}
        onNext={() => goTo(currentIndex + 1)}
      />

      <div className="rcd-main">
        <aside className="rcd-side">
          <ContractInfoPanel
            contract={contract}
            loading={contractLoading}
            onEdit={onEdit}
            onCheckOut={handleCheckOut}
            checkingOut={loading}
          />
        </aside>

        <section className="rcd-content">
          <div className="rcd-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "bills"}
              className={`rcd-tab${tab === "bills" ? " on" : ""}`}
              onClick={() => setTab("bills")}
            >
              账单列表
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "payments"}
              className={`rcd-tab${tab === "payments" ? " on" : ""}`}
              onClick={() => {
                setTab("payments");
                setPaymentsVisited(true);
              }}
            >
              付款记录
            </button>
          </div>

          {contract ? (
            <>
              {/* 两个 tab 都保持挂载，切换不丢状态；付款记录首次访问才加载 */}
              <div hidden={tab !== "bills"}>
                <RentBillList
                  key={contract._id}
                  contract={contract}
                  checkContract={checkContract}
                />
              </div>
              {paymentsVisited && (
                <div hidden={tab !== "payments"}>
                  <PaymentRecordList key={contract._id} contract={contract} />
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Spin />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
